/**
 * Chat Group Client Service
 *
 * Browser fetch wrappers for group chat APIs.
 *
 * Primary responsibilities:
 * - Call create/convert group chat endpoint
 * - Fetch and mutate invite links
 * - Fetch and mutate group members
 *
 * Dependencies:
 * - Group chat API routes
 * - Group chat shared types
 */

import type {
  CreateGroupChatResponse,
  GroupInviteLinkResponse,
  GroupMembersActionPayload,
  GroupChatMembersResponse,
  GroupChatResponseMode
} from '@/types/chat-groups'
import type { Tables } from '@/supabase/types'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'

type ApiError = {
  error?: string
}

type GroupResponseModeUpdateResponse = {
  success: boolean
  chat: Tables<'chats'>
  responseMode: GroupChatResponseMode
}

async function readResponse<T>(response: Response, fallback: string) {
  const payload = (await response.json().catch(() => ({}))) as T & ApiError

  if (!response.ok) {
    throw new Error(payload.error || fallback)
  }

  return payload
}

export async function createGroupChatFromSource(
  sourceChatId: string,
  responseMode: GroupChatResponseMode = 'always'
) {
  const response = await authenticatedFetch('/api/chat/groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ sourceChatId, responseMode })
  })

  return readResponse<CreateGroupChatResponse>(response, 'Failed to convert chat to group')
}

export async function getGroupInviteLink(chatId: string) {
  const response = await authenticatedFetch(`/api/chat/groups/${chatId}/invite-link`, {
    cache: 'no-store'
  })
  return readResponse<GroupInviteLinkResponse>(response, 'Failed to load invite link')
}

export async function regenerateGroupInviteLink(chatId: string) {
  const response = await authenticatedFetch(`/api/chat/groups/${chatId}/invite-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ action: 'regenerate' })
  })

  return readResponse<GroupInviteLinkResponse>(response, 'Failed to regenerate invite link')
}

export async function getGroupMembers(chatId: string) {
  const response = await authenticatedFetch(`/api/chat/groups/${chatId}/members`, {
    cache: 'no-store'
  })
  return readResponse<GroupChatMembersResponse>(response, 'Failed to load group members')
}

export async function updateGroupResponseMode(
  chatId: string,
  responseMode: GroupChatResponseMode
) {
  const response = await authenticatedFetch(`/api/chat/groups/${chatId}/members`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      action: 'update_response_mode',
      responseMode
    })
  })

  return readResponse<GroupResponseModeUpdateResponse>(
    response,
    'Failed to update group response mode'
  )
}

export async function runGroupMembersAction(
  chatId: string,
  payload: GroupMembersActionPayload
) {
  const response = await authenticatedFetch(`/api/chat/groups/${chatId}/members`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  })

  return readResponse<{
    success: boolean
    participantCount?: number
    responseMode?: GroupChatResponseMode
    chat?: Tables<'chats'>
  }>(
    response,
    'Failed to update group members'
  )
}
